import { findHeadingId, isHashOfRoute } from '~/utils/docs-heading-hash';

/**
 * Scroll-spy and smooth-scroll helpers for docs TOC sidebars.
 *
 * With `autoBind`, the listeners bind when the scroll element appears. A page that renders the
 * element only after its content loads has no element at mount.
 */
export function useDocsTocScroll(mainScrollRef: Ref<HTMLElement | null>, options?: { autoBind?: boolean }) {
  const route = useRoute();

  const activeTocId = ref<string | null>(null);
  const tocSpyLocked = ref(false);
  const tocPendingTargetId = ref<string | null>(null);
  const tocScrollFallbackTimer = ref<ReturnType<typeof setTimeout> | null>(null);

  const TOC_SCAN_LINE_RATIO = 0.28;

  function finishTocProgrammaticScroll() {
    const id = tocPendingTargetId.value;
    tocSpyLocked.value = false;
    tocPendingTargetId.value = null;
    if (tocScrollFallbackTimer.value !== null) {
      clearTimeout(tocScrollFallbackTimer.value);
      tocScrollFallbackTimer.value = null;
    }
    if (id) {
      activeTocId.value = id;
    }
  }

  function startTocProgrammaticScroll(targetId: string) {
    tocSpyLocked.value = true;
    tocPendingTargetId.value = targetId;
    activeTocId.value = targetId;
    if (tocScrollFallbackTimer.value !== null) {
      clearTimeout(tocScrollFallbackTimer.value);
    }
    tocScrollFallbackTimer.value = setTimeout(() => finishTocProgrammaticScroll(), 8000);
  }

  function onMainScrollEnd() {
    if (!tocSpyLocked.value) {
      return;
    }
    finishTocProgrammaticScroll();
  }

  function updateActiveHeading() {
    if (tocSpyLocked.value) {
      return;
    }

    const root = mainScrollRef.value;
    if (!root) {
      return;
    }
    const headings = [...root.querySelectorAll('h2[id], h3[id]')] as HTMLElement[];
    if (!headings.length) {
      activeTocId.value = null;
      return;
    }

    const rootRect = root.getBoundingClientRect();
    const lineY = rootRect.top + Math.min(160, Math.max(72, rootRect.height * TOC_SCAN_LINE_RATIO));

    const aboveLine = headings.filter(h => h.getBoundingClientRect().top <= lineY);
    if (!aboveLine.length) {
      activeTocId.value = headings[0].id;
      return;
    }

    const best = aboveLine.reduce((a, b) => (a.getBoundingClientRect().top >= b.getBoundingClientRect().top ? a : b));
    activeTocId.value = best.id;
  }

  function scrollMainToId(id: string) {
    const root = mainScrollRef.value;
    if (!root || !id) {
      return;
    }
    const el = Array.from(root.querySelectorAll('[id]')).find(candidate => candidate.id === id);
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /**
   * The hash to look up for `routeHash`. A TOC click knows its id, and route.hash holds that id
   * exactly. Otherwise the URL hash comes first: vue-router decodes the hash of a string location
   * twice, so route.hash can name another heading or none. route.hash is the fallback when the
   * URL hash belongs to another navigation.
   */
  function hashToFind(routeHash: string): string {
    if (tocPendingTargetId.value !== null && routeHash === `#${tocPendingTargetId.value}`) {
      return routeHash;
    }
    const rawHash = window.location.hash;
    return isHashOfRoute(rawHash, routeHash) ? rawHash : routeHash;
  }

  /**
   * Scrolls to the heading of a URL hash. The rendered page must contain the heading, because
   * the hash can take several forms (see docs-heading-hash.ts) and only the DOM tells which one
   * matches.
   */
  function scrollMainToHash(hash: string | null | undefined) {
    const root = mainScrollRef.value;
    if (!root || !hash) {
      return;
    }
    const id = findHeadingId(root, hashToFind(hash));
    if (!id) {
      return;
    }
    startTocProgrammaticScroll(id);
    scrollMainToId(id);
  }

  function onTocClick(id: string, options?: { updateHash?: boolean }) {
    startTocProgrammaticScroll(id);
    scrollMainToId(id);
    if (options?.updateHash) {
      navigateTo({ hash: `#${id}`, replace: true });
    }
  }

  function bindScrollListeners() {
    const el = mainScrollRef.value;
    if (!el) {
      return;
    }
    el.addEventListener('scroll', updateActiveHeading, { passive: true });
    el.addEventListener('scrollend', onMainScrollEnd);

    if (route.hash) {
      nextTick(() => scrollMainToHash(route.hash));
    } else {
      updateActiveHeading();
    }
  }

  function unbindScrollListeners() {
    const el = mainScrollRef.value;
    el?.removeEventListener('scroll', updateActiveHeading);
    el?.removeEventListener('scrollend', onMainScrollEnd);
    if (tocScrollFallbackTimer.value !== null) {
      clearTimeout(tocScrollFallbackTimer.value);
      tocScrollFallbackTimer.value = null;
    }
  }

  if (options?.autoBind) {
    // A first visit, for example from a link that opens a new tab, also scrolls to the hash.
    watch(
      mainScrollRef,
      el => {
        if (el) {
          bindScrollListeners();
        }
      },
      { flush: 'post' },
    );
  }

  return {
    activeTocId,
    startTocProgrammaticScroll,
    finishTocProgrammaticScroll,
    updateActiveHeading,
    scrollMainToId,
    scrollMainToHash,
    onTocClick,
    bindScrollListeners,
    unbindScrollListeners,
  };
}
